import classNames from "clsx";
import request from "graphql-request";
import { GetStaticPaths, GetStaticPropsContext } from "next";
import Head from "next/head";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";

import { TextBlock } from "@shared/components";
import { urls } from "@shared/constants";
import { Button, Text } from "@shared/elements";
import { buildImageUrl, docTitle } from "@shared/utils";

import {
  JuryCallPage,
  TCallPageJurors,
  TCallPageJury,
  TJuryCallPage,
} from "__generated__/types";

import * as constants from "src/constants";
import bundleMessages from "src/utils/bundleMessages";
import { getLanguageDirection } from "src/utils/getLanguageDirection";

function Juror({
  juror,
  containerClasses,
}: {
  juror: TCallPageJurors;
  containerClasses?: string;
}) {
  const t = useTranslations(`${constants.MESSAGE_PREFIX}pages.Jury`);
  const [showFullText, setShowFullText] = useState(false);
  const bioTextSize = useMemo(() => juror?.bio?.length || 0, [juror]);
  const isLongText = useMemo(() => bioTextSize > 500, [bioTextSize]);
  const imgUrl = `${process.env.NEXT_PUBLIC_CDN_URL}${juror.avatar}`;

  return (
    <div
      className={classNames(
        "flex flex-col md:flex-row gap-y-6 md:gap-x-6 md:gap-y-0 pb-8 border-b-[1px] border-b-gray-200",
        containerClasses,
      )}
    >
      {juror.avatar && (
        <picture className="w-full basis-[225px] shrink-0 grow-0 md:w-[225px] md:h-[225px]">
          <source
            srcSet={buildImageUrl(imgUrl, {
              w: 420,
              h: 420,
              rt: "fill",
            })}
            media="(min-width: 768px)"
          />
          <img
            className="w-full"
            srcSet={buildImageUrl(imgUrl, {
              w: 900,
              h: 900,
              rt: "fill",
            })}
            alt={juror.name ?? ""}
          />
        </picture>
      )}
      <div className="flex flex-col gap-y-4">
        <div>
          <Text size="lg" weight="semibold" className="leading-7">
            {juror.name}
          </Text>
          <Text size="lg" weight="semibold" color="muted">
            {juror.jobTitle}
          </Text>
        </div>
        {juror.bio && (
          <div className="flex flex-col gap-y-4">
            <TextBlock
              content={juror.bio}
              containerClasses={classNames(
                isLongText && !showFullText
                  ? "line-clamp-8"
                  : "line-clamp-none",
              )}
            />
            {isLongText && (
              <Button
                type="button"
                variant="text"
                className="!justify-start py-0 text-base text-primary-600 hover:text-primary-600"
                onClick={() => setShowFullText(!showFullText)}
              >
                {showFullText ? t("showLessTextBtn") : t("showFullTextBtn")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type Props = {
  data: TJuryCallPage;
};

export default function JuryPage({ data }: Props) {
  const t = useTranslations(`${constants.MESSAGE_PREFIX}pages.Jury`);
  return (
    <>
      <Head>
        <title key="title">
          {docTitle(
            data.callPage?.jurySectionTitle || t("documentTitle"),
            data.callPage?.call.title.en,
          )}
        </title>
        <meta
          property="og:title"
          content={docTitle(
            data.callPage?.jurySectionTitle || t("documentTitle"),
            data.callPage?.call.title.en,
          )}
          key="og-title"
        />
        <meta
          property="og:url"
          content={`${urls.callPage(data.callPage?.slug ?? "")}/jury`}
          key="og-url"
        />
      </Head>
      <div
        className="flex flex-col items-center mx-auto py-10 lg:py-14"
        dir={getLanguageDirection(data.callPage?.language)}
      >
        <div className="flex flex-col items-center gap-y-8 lg:gap-y-14">
          {data.callPage?.jury?.map((item: TCallPageJury, index: number) => (
            <div
              className="flex flex-col gap-y-8 md:w-[700px] max-w-screen-md mx-4"
              key={index}
            >
              <Text weight="bold" size="3xl">
                {item.title}
              </Text>
              {item.jurors?.map((juror: TCallPageJurors, jIndex: number) => (
                <Juror
                  juror={juror}
                  key={jIndex}
                  containerClasses={"last:border-b-0 last:pb-0"}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export const getStaticPaths: GetStaticPaths<{ id: string }> = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export async function getStaticProps({
  locale,
  params,
}: GetStaticPropsContext) {
  const messages = await bundleMessages(locale, [
    `${constants.MESSAGE_PREFIX}pages.Jury`,
  ]);

  const slug: string = params?.slug as string;

  const data: TJuryCallPage | null = await request(
    `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
    JuryCallPage,
    { slug },
  );

  if (!data?.callPage) {
    return {
      notFound: true,
      revalidate: 60,
    };
  }
  return {
    props: {
      data,
      messages,
    },
    revalidate: 60,
  };
}
